import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatRupees, todayISO } from "@/lib/format";
import { ArrowLeft, Landmark, HandCoins, Building2, Coffee, TrendingUp, CalendarPlus, Wallet, HandHelping } from "lucide-react";

const TYPES = [
  { key: "land", label: "Land", desc: "Property. No monthly income. Value grows.", icon: Landmark },
  { key: "loan", label: "Loan Given", desc: "Money lent at interest — monthly payouts to us.", icon: HandCoins },
  { key: "loan_taken", label: "Loan Taken", desc: "Money we borrowed — monthly interest we pay.", icon: HandHelping },
  { key: "company_share", label: "Company Share", desc: "Equity in a company.", icon: Building2 },
  { key: "business_franchise", label: "Business / Franchise", desc: "Cafe, retail outlet, etc.", icon: Coffee },
  { key: "sebi_startup_fund", label: "SEBI Startup Fund", desc: "SEBI-approved AIF / startup fund with expected IRR.", icon: TrendingUp },
  { key: "sip", label: "SIP", desc: "Monthly systematic investment plan.", icon: CalendarPlus },
  { key: "lumpsum", label: "Lumpsum / Long-term", desc: "MF, FD, bonds — single deposit that grows.", icon: Wallet },
];

export default function AddInvestment() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [form, setForm] = useState({
    name: "", code: "", invested_at: todayISO(), amount_invested: "", current_value: "",
    expected_return_percent: "", notes: "", meta: {},
  });
  const [busy, setBusy] = useState(false);

  const setMeta = (k, v) => setForm((f) => ({ ...f, meta: { ...f.meta, [k]: v } }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        type,
        amount_invested: Number(form.amount_invested),
        current_value: Number(form.current_value || form.amount_invested),
        expected_return_percent: Number(form.expected_return_percent || 0),
      };
      const r = await api.post("/investments", payload);
      toast.success("Investment created");
      nav(`/investments/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const isLoan = type === "loan" || type === "loan_taken";
  const isLoanTaken = type === "loan_taken";
  const expectedInterest = isLoan && form.amount_invested && form.meta.interest_rate ?
    (Number(form.amount_invested) * Number(form.meta.interest_rate) / 12 / 100) : 0;

  const expectedAnnual = form.amount_invested && form.expected_return_percent ?
    (Number(form.amount_invested) * Number(form.expected_return_percent) / 100) : 0;

  return (
    <div className="max-w-3xl space-y-8">
      <Link to="/investments" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <div>
        <div className="eyebrow">Step {step} of 2</div>
        <h1 className="mt-2 font-editorial font-light text-[34px] leading-[1.05] sm:text-4xl md:text-5xl text-slate-900 tracking-[-0.035em]" data-testid="add-inv-title">
          {step === 1 ? <>Choose <em className="italic">type.</em></> : <>Fill <em className="italic">details.</em></>}
        </h1>
      </div>

      {step === 1 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="type-grid">
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => { setType(t.key); setStep(2); }}
              data-testid={`choose-type-${t.key}`}
              className="text-left border border-slate-200 p-5 hover-lift bg-white rounded-md group"
            >
              <t.icon className="h-7 w-7 text-red-600 mb-3" strokeWidth={1.5} />
              <h3 className="font-medium text-[15px] text-slate-900">{t.label}</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t.desc}</p>
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div className="border border-slate-200 p-6 bg-white space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="f-name" className="mt-2" />
              </div>
              <div>
                <Label>Project code <span className="text-slate-400 text-[10px]">(optional — used in doc numbers)</span></Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, "") })} placeholder="e.g. LND01, CAFE" maxLength={10} data-testid="f-code" className="mt-2 font-mono-fin" />
              </div>
              <div>
                <Label>Invested on</Label>
                <Input type="date" value={form.invested_at} onChange={(e) => setForm({ ...form, invested_at: e.target.value })} data-testid="f-date" className="mt-2" />
              </div>
              <div>
                <Label>Amount invested (Rs)</Label>
                <Input type="number" value={form.amount_invested} onChange={(e) => setForm({ ...form, amount_invested: e.target.value })} data-testid="f-amount" className="mt-2" />
              </div>
              <div>
                <Label>Current value (Rs)</Label>
                <Input type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} data-testid="f-current" className="mt-2" />
              </div>
            </div>

            {type === "land" && (
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><Label>Location</Label><Input onChange={(e) => setMeta("location", e.target.value)} className="mt-2" data-testid="f-location" /></div>
                <div><Label>Survey number</Label><Input onChange={(e) => setMeta("survey_number", e.target.value)} className="mt-2" data-testid="f-survey" /></div>
                <div><Label>Purchase price (Rs)</Label><Input type="number" onChange={(e) => setMeta("purchase_price", Number(e.target.value))} className="mt-2" /></div>
                <div><Label>Stamp duty (Rs)</Label><Input type="number" onChange={(e) => setMeta("stamp_duty", Number(e.target.value))} className="mt-2" /></div>
                <div><Label>Registration cost (Rs)</Label><Input type="number" onChange={(e) => setMeta("registration", Number(e.target.value))} className="mt-2" /></div>
                <div><Label>Monthly rent (Rs)</Label><Input type="number" onChange={(e) => setMeta("monthly_rent", Number(e.target.value))} className="mt-2" /></div>
              </div>
            )}

            {isLoan && (
              <>
                <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                  <div><Label>{isLoanTaken ? "Lender name" : "Borrower name"}</Label><Input onChange={(e) => setMeta("borrower_name", e.target.value)} className="mt-2" data-testid="f-borrower" /></div>
                  <div><Label>Phone</Label><Input onChange={(e) => setMeta("phone", e.target.value)} className="mt-2" /></div>
                  <div><Label>PAN / Aadhaar</Label><Input onChange={(e) => setMeta("pan_aadhaar", e.target.value)} className="mt-2" /></div>
                  <div><Label>Interest rate (% p.a.)</Label><Input type="number" defaultValue="18" onChange={(e) => setMeta("interest_rate", Number(e.target.value))} className="mt-2" data-testid="f-rate" /></div>
                  <div><Label>Tenure (months)</Label><Input type="number" onChange={(e) => setMeta("tenure_months", Number(e.target.value))} className="mt-2" /></div>
                  <div><Label>Payment due day (1-28)</Label><Input type="number" defaultValue="5" onChange={(e) => setMeta("payment_due_day", Number(e.target.value))} className="mt-2" /></div>
                  <div className="sm:col-span-2"><Label>{isLoanTaken ? "Security we gave" : "Collateral / security"}</Label><Input onChange={(e) => setMeta("collateral", e.target.value)} className="mt-2" /></div>
                </div>
                {expectedInterest > 0 && (
                  <div className={`p-3 border text-sm ${isLoanTaken ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-red-50 border-red-100 text-red-900"}`} data-testid="expected-interest">
                    {isLoanTaken ? "Interest we'll pay monthly:" : "Expected monthly interest:"} <span className="font-mono-fin font-semibold">{formatRupees(expectedInterest)}</span>
                  </div>
                )}
              </>
            )}

            {type === "company_share" && (
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><Label>Company name</Label><Input onChange={(e) => setMeta("company_name", e.target.value)} className="mt-2" data-testid="f-company" /></div>
                <div><Label>Our shareholding (%)</Label><Input type="number" onChange={(e) => setMeta("shareholding_percent", Number(e.target.value))} className="mt-2" /></div>
                <div>
                  <Label>Profit share frequency</Label>
                  <Select onValueChange={(v) => setMeta("profit_share_frequency", v)}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {type === "business_franchise" && (
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><Label>Brand / franchise</Label><Input onChange={(e) => setMeta("brand", e.target.value)} className="mt-2" data-testid="f-brand" /></div>
                <div><Label>Outlet location</Label><Input onChange={(e) => setMeta("outlet", e.target.value)} className="mt-2" /></div>
                <div><Label>Total project cost (Rs)</Label><Input type="number" onChange={(e) => setMeta("total_project_cost", Number(e.target.value))} className="mt-2" /></div>
                <div><Label>Our stake (%)</Label><Input type="number" defaultValue="12.5" onChange={(e) => setMeta("stake_percent", Number(e.target.value))} className="mt-2" data-testid="f-stake" /></div>
                <div><Label>Monthly rent (Rs)</Label><Input type="number" onChange={(e) => setMeta("monthly_rent", Number(e.target.value))} className="mt-2" /></div>
                <div><Label>Breakeven target (Rs)</Label><Input type="number" onChange={(e) => setMeta("breakeven_target", Number(e.target.value))} className="mt-2" /></div>
              </div>
            )}

            {type === "sebi_startup_fund" && (
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><Label>Fund name</Label><Input onChange={(e) => setMeta("fund_name", e.target.value)} className="mt-2" data-testid="f-fund-name" /></div>
                <div><Label>Fund manager / AMC</Label><Input onChange={(e) => setMeta("fund_manager", e.target.value)} className="mt-2" /></div>
                <div><Label>SEBI registration no.</Label><Input onChange={(e) => setMeta("sebi_regn", e.target.value)} className="mt-2" /></div>
                <div><Label>Category (Cat-I / II / III)</Label><Input onChange={(e) => setMeta("aif_category", e.target.value)} className="mt-2" /></div>
                <div><Label>Commitment (Rs)</Label><Input type="number" onChange={(e) => setMeta("commitment", Number(e.target.value))} className="mt-2" /></div>
                <div><Label>Tenure (years)</Label><Input type="number" onChange={(e) => setMeta("tenure_years", Number(e.target.value))} className="mt-2" /></div>
              </div>
            )}

            {type === "sip" && (
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><Label>Fund / scheme name</Label><Input onChange={(e) => setMeta("fund_name", e.target.value)} className="mt-2" data-testid="f-sip-name" /></div>
                <div><Label>Folio no.</Label><Input onChange={(e) => setMeta("folio_no", e.target.value)} className="mt-2" /></div>
                <div><Label>Monthly instalment (Rs)</Label><Input type="number" onChange={(e) => setMeta("monthly_amount", Number(e.target.value))} className="mt-2" data-testid="f-sip-amount" /></div>
                <div><Label>SIP date (day of month)</Label><Input type="number" defaultValue="5" onChange={(e) => setMeta("sip_day", Number(e.target.value))} className="mt-2" /></div>
                <div><Label>Tenure (months)</Label><Input type="number" onChange={(e) => setMeta("tenure_months", Number(e.target.value))} className="mt-2" /></div>
                <div><Label>Frequency</Label>
                  <Select onValueChange={(v) => setMeta("frequency", v)}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="Monthly" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {type === "lumpsum" && (
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><Label>Instrument</Label>
                  <Select onValueChange={(v) => setMeta("instrument", v)}>
                    <SelectTrigger className="mt-2" data-testid="f-instrument"><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mutual_fund">Mutual Fund</SelectItem>
                      <SelectItem value="fixed_deposit">Fixed Deposit</SelectItem>
                      <SelectItem value="bond">Bond</SelectItem>
                      <SelectItem value="gold">Gold / Sovereign Gold Bond</SelectItem>
                      <SelectItem value="ppf">PPF / EPF</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Provider (Bank / AMC)</Label><Input onChange={(e) => setMeta("provider", e.target.value)} className="mt-2" /></div>
                <div><Label>Account / folio no.</Label><Input onChange={(e) => setMeta("account_no", e.target.value)} className="mt-2" /></div>
                <div><Label>Maturity date</Label><Input type="date" onChange={(e) => setMeta("maturity_date", e.target.value)} className="mt-2" /></div>
                <div><Label>Tenure (months)</Label><Input type="number" onChange={(e) => setMeta("tenure_months", Number(e.target.value))} className="mt-2" /></div>
              </div>
            )}

            {/* Common: expected return */}
            {type !== "land" && (
              <div className="pt-4 border-t border-slate-100">
                <Label>Expected annual return (%) — optional</Label>
                <Input
                  type="number" step="0.1"
                  value={form.expected_return_percent}
                  onChange={(e) => setForm({ ...form, expected_return_percent: e.target.value })}
                  className="mt-2 max-w-xs"
                  data-testid="f-expected-return"
                  placeholder={type === "loan" ? "18" : type === "sebi_startup_fund" ? "20" : type === "sip" ? "12" : "8"}
                />
                {expectedAnnual > 0 && (
                  <div className="mt-3 text-xs text-slate-600">
                    ≈ <span className="font-mono-fin font-medium text-slate-900">{formatRupees(expectedAnnual)}</span> per year ·
                    ≈ <span className="font-mono-fin font-medium text-slate-900">{formatRupees(expectedAnnual / 12)}</span> per month
                  </div>
                )}
              </div>
            )}

            <div className="pt-4 border-t border-slate-100">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" data-testid="f-notes" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-none">Back</Button>
            <Button onClick={submit} disabled={busy || !form.name || !form.amount_invested} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="save-investment">
              {busy ? "Saving..." : "Save Investment"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
